import React from 'react';
import styled from 'styled-components';

import { getMediaUrl } from '../../helpers';
import { useLexicon } from '../../hooks/useLexicon';

const extractPhoto = (player) => {
  const photos = player?.photos?.data ?? player?.photos;
  if (!Array.isArray(photos) || photos.length === 0) return null;
  const raw = photos[0]?.full || photos[0]?.image || null;
  return raw ? getMediaUrl(raw) : null;
};

const extractNationality = (player) => {
  const nats = player?.nationality?.data ?? player?.nationality;
  if (!Array.isArray(nats) || nats.length === 0) return null;
  const n = nats[0];
  return {
    ...n,
    flag: n?.flag ? getMediaUrl(n.flag) : null,
  };
};

const extractPosition = (player) => {
  const positions = player?.position?.data ?? player?.position;
  if (!Array.isArray(positions) || positions.length === 0) return null;
  return positions[0]?.name || null;
};

const extractTeam = (player) => {
  const team = player?.team;
  if (!team) return null;
  const rawLogo = team.logo && team.logo_base
    ? `${String(team.logo_base).replace(/\/$/, '')}/${String(team.logo).replace(/^\//, '')}`
    : null;
  return {
    name: team.name,
    logo: rawLogo ? getMediaUrl(rawLogo) : null,
  };
};

const PlayerIdentityCard = ({ player, displayName }) => {
  const l = useLexicon();
  if (!player) return null;

  const name = displayName || player.display_name || [player.name, player.surname].filter(Boolean).join(' ');
  const photo = extractPhoto(player);
  const nationality = extractNationality(player);
  const position = extractPosition(player);
  const team = extractTeam(player);

  const [firstName, ...rest] = (name || '').split(' ');
  const lastName = rest.join(' ');

  return (
    <Card>
      <Top>
        <PhotoWrap>
          {photo ? <PlayerPhoto src={photo} alt={name} /> : <PhotoPlaceholder />}
        </PhotoWrap>
        <Identity>
          {nationality && (
            <Nationality>
              {nationality.flag && <Flag src={nationality.flag} alt="" />}
              <NationalityName>{nationality.name}</NationalityName>
            </Nationality>
          )}
          <Name>
            <NameLine>{firstName}</NameLine>
            {lastName && <NameLine>{lastName}</NameLine>}
          </Name>
          {position && <Position>{position}</Position>}
        </Identity>
      </Top>

      {team && (
        <>
          <Divider />
          <Footer>
            <FooterLabel>{l(1004)}</FooterLabel>
            <FooterValue>
              {team.logo && <TeamLogo src={team.logo} alt="" />}
              <span>{team.name}</span>
            </FooterValue>
          </Footer>
        </>
      )}
    </Card>
  );
};

export default PlayerIdentityCard;

const Card = styled.div`
  background: ${(props) => props.theme.row};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Top = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const PhotoWrap = styled.div`
  flex-shrink: 0;
  width: 120px;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  background: ${(props) => props.theme.black};
`;

const PlayerPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PhotoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: ${(props) => props.theme.black};
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const Nationality = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${(props) => props.theme.blueish2};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
`;

const Flag = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
`;

const NationalityName = styled.span``;

const Name = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.05;
`;

const NameLine = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 28px;
  color: ${(props) => props.theme.white};
`;

const Position = styled.div`
  margin-top: 2px;
  color: ${(props) => props.theme.blueish2};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 16px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${(props) => props.theme.borderColor};
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FooterLabel = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
`;

const FooterValue = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 18px;
  color: ${(props) => props.theme.white};
`;

const TeamLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;
